import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ICreateVoiceAgentFormData, IVoiceSipTrunk } from '@/@types/voiceAgents';

interface IAgentFormLimitsTabProps {
  // LOCAL_TYPE_JUSTIFIED: single-use props interface for this tab sub-component
  form: ICreateVoiceAgentFormData;
  setField: (
    field: string,
    value: string | number | boolean | string[] | Record<string, number> | undefined
  ) => void;
  activeSipTrunks: IVoiceSipTrunk[];
}

export function AgentFormLimitsTab({ form, setField, activeSipTrunks }: IAgentFormLimitsTabProps) {
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="max-tool-calls">Max Tool Calls</Label>
          <Input
            id="max-tool-calls"
            type="number"
            value={form.maxToolCallsPerCall}
            onChange={e => setField('maxToolCallsPerCall', Number(e.target.value))}
            min={1}
            max={100}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="max-duration">Max Duration (min)</Label>
          <Input
            id="max-duration"
            type="number"
            value={Math.round(form.maxCallDurationMs / 60000)}
            onChange={e => setField('maxCallDurationMs', Number(e.target.value) * 60000)}
            min={1}
            max={120}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="max-denied">Max Denied Before Transfer</Label>
          <Input
            id="max-denied"
            type="number"
            value={form.maxDeniedBeforeTransfer}
            onChange={e => setField('maxDeniedBeforeTransfer', Number(e.target.value))}
            min={1}
            max={20}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="max-sentences">Max Reply Sentences</Label>
          <Input
            id="max-sentences"
            type="number"
            value={form.maxReplySentences}
            onChange={e => setField('maxReplySentences', Number(e.target.value))}
            min={1}
            max={10}
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="text-sm font-medium mb-3">Escalation</h3>
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label>Human Escalation</Label>
            <p className="text-xs text-muted-foreground">Allow transfer to a human agent</p>
          </div>
          <Switch
            checked={form.humanEscalationEnabled}
            onCheckedChange={v => setField('humanEscalationEnabled', v)}
          />
        </div>
        {form.humanEscalationEnabled && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="escalation-phone">Escalation Phone</Label>
              <Input
                id="escalation-phone"
                value={form.escalationPhoneNumber ?? ''}
                onChange={e => setField('escalationPhoneNumber', e.target.value || undefined)}
                placeholder="+14155551234"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="escalation-trunk">Escalation SIP Trunk</Label>
              <Select
                value={form.escalationSipTrunkId ?? 'none'}
                onValueChange={v => setField('escalationSipTrunkId', v === 'none' ? undefined : v)}
              >
                <SelectTrigger id="escalation-trunk" className="w-full">
                  <SelectValue placeholder="Select trunk" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {activeSipTrunks.map(t => (
                    <SelectItem key={t._id} value={t._id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <h3 className="text-sm font-medium mb-3">Cost Rates</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Optional — leave empty if not tracking costs
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="stt-rate">STT Rate ($/sec)</Label>
            <Input
              id="stt-rate"
              type="number"
              step="0.0001"
              value={form.costRates?.sttRatePerSecond ?? ''}
              onChange={e => {
                const val = e.target.value;
                if (val === '') {
                  setField('costRates', undefined);
                  return;
                }
                setField('costRates', {
                  sttRatePerSecond: Number(val),
                  llmInputPer1kTokens: form.costRates?.llmInputPer1kTokens ?? 0,
                  llmOutputPer1kTokens: form.costRates?.llmOutputPer1kTokens ?? 0,
                  ttsRatePerCharacter: form.costRates?.ttsRatePerCharacter ?? 0,
                  sipRatePerMinute: form.costRates?.sipRatePerMinute ?? 0,
                });
              }}
              placeholder="0.0001"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sip-rate">SIP Rate ($/min)</Label>
            <Input
              id="sip-rate"
              type="number"
              step="0.001"
              value={form.costRates?.sipRatePerMinute ?? ''}
              onChange={e => {
                const val = e.target.value;
                if (val === '') {
                  setField('costRates', undefined);
                  return;
                }
                setField('costRates', {
                  sttRatePerSecond: form.costRates?.sttRatePerSecond ?? 0,
                  llmInputPer1kTokens: form.costRates?.llmInputPer1kTokens ?? 0,
                  llmOutputPer1kTokens: form.costRates?.llmOutputPer1kTokens ?? 0,
                  ttsRatePerCharacter: form.costRates?.ttsRatePerCharacter ?? 0,
                  sipRatePerMinute: Number(val),
                });
              }}
              placeholder="0.01"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
